import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/lib/auth";
import { apiClient, readError } from "@/lib/api";
import SEO from "@/components/SEO";
import { getSEO } from "@/lib/seo-config";
import { toast } from "sonner";
import { Crown, ShieldCheck, BadgeCheck, Sparkles, ArrowUpRight, Check, Lock, Award } from "lucide-react";

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });

export default function Premium() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(null); // "premium" | "gold" | null

  const refresh = async () => {
    try {
      const { data } = await apiClient.get("/premium/status");
      setStatus(data);
    } catch (e) {
      toast.error(readError(e));
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const subscribe = async (tier) => {
    setBusy(tier);
    try {
      const { data } = await apiClient.post("/premium/subscribe", {
        origin_url: window.location.origin,
        tier,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(readError(e));
      setBusy(null);
    }
  };

  if (user?.role !== "artisan") {
    return (
      <div className="min-h-screen bg-[#F7F7F9]">
        <Navbar />
        <div className="max-w-2xl mx-auto px-6 py-20 text-center">
          <div className="border-2 border-[#0A0A0B] bg-white p-10">
            <Crown className="w-10 h-10 mx-auto text-[#FF4D00]" />
            <h1 className="display text-4xl mt-4">Réservé aux artisans</h1>
            <p className="text-zinc-600 mt-3">Les packs Batisur sont destinés aux professionnels du bâtiment.</p>
          </div>
        </div>
      </div>
    );
  }

  const premiumPrice = status?.premium_price ?? status?.price ?? 29;
  const premiumDays = status?.premium_days ?? status?.days ?? 30;
  const goldPrice = status?.gold_price ?? 49;
  const goldDays = status?.gold_days ?? 30;
  const goldMin = status?.gold_min_completed ?? 10;
  const elig = status?.gold_eligibility || { eligible: false, siret_verified: false, completed_count: 0, required: goldMin };

  return (
    <div className="min-h-screen bg-[#F7F7F9]">
      <SEO cfg={getSEO("/pro/premium")} />
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00]">// Packs artisans Batisur</div>
        <h1 className="display text-5xl mt-2">Faites tourner votre carnet de commandes.</h1>
        <p className="text-zinc-600 mt-3 max-w-2xl">
          Deux niveaux de visibilité. <strong>Premium</strong> pour démarrer fort, <strong>Or</strong> pour les artisans vérifiés et expérimentés qui veulent dominer leur zone.
        </p>

        {/* Active banners */}
        {(status?.is_gold || status?.is_premium) && (
          <div className="mt-8 grid md:grid-cols-2 gap-4">
            {status?.is_gold && (
              <div className="border-2 border-amber-600 bg-amber-50 p-5 flex items-center justify-between gap-3" data-testid="gold-active-banner">
                <div className="flex items-center gap-3">
                  <Award className="w-8 h-8 text-amber-700" />
                  <div>
                    <div className="display text-xl text-amber-900">Pack Or actif</div>
                    <div className="text-xs text-amber-800 mt-1">Jusqu'au <strong>{fmtDate(status.gold_until)}</strong></div>
                  </div>
                </div>
                <button onClick={() => subscribe("gold")} disabled={busy} className="btn-anthracite text-sm" data-testid="gold-renew">
                  +{goldDays}j
                </button>
              </div>
            )}
            {status?.is_premium && !status?.is_gold && (
              <div className="border-2 border-emerald-700 bg-emerald-50 p-5 flex items-center justify-between gap-3" data-testid="premium-active-banner">
                <div className="flex items-center gap-3">
                  <BadgeCheck className="w-8 h-8 text-emerald-700" />
                  <div>
                    <div className="display text-xl text-emerald-900">Premium actif</div>
                    <div className="text-xs text-emerald-800 mt-1">Jusqu'au <strong>{fmtDate(status.premium_until)}</strong></div>
                  </div>
                </div>
                <button onClick={() => subscribe("premium")} disabled={busy} className="btn-anthracite text-sm" data-testid="premium-renew">
                  +{premiumDays}j
                </button>
              </div>
            )}
          </div>
        )}

        <div className="mt-10 grid lg:grid-cols-3 gap-6">
          {/* Free plan */}
          <div className="border-2 border-[#0A0A0B] bg-white p-7 flex flex-col">
            <div className="mono text-[11px] uppercase tracking-widest text-zinc-500">Plan actuel</div>
            <div className="display text-3xl mt-2">Standard</div>
            <div className="display text-5xl mt-4">0€</div>
            <div className="text-xs text-zinc-500 mono uppercase tracking-widest mt-1">à vie</div>

            <ul className="mt-7 space-y-3 text-sm flex-1">
              {["Profil public", "Devis illimités", "Paiement séquestré Stripe", "Messagerie intégrée"].map((b) => (
                <li key={b} className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-zinc-500" /> {b}
                </li>
              ))}
            </ul>
          </div>

          {/* Premium plan */}
          <div className="border-2 border-[#FF4D00] bg-[#0A0A0B] text-white p-7 relative flex flex-col">
            <div className="absolute -top-3 left-7 bg-[#FF4D00] px-3 py-1 mono text-[10px] uppercase tracking-widest font-semibold">
              Populaire
            </div>
            <div className="flex items-center gap-2 mono text-[11px] uppercase tracking-widest text-[#FF4D00]">
              <Sparkles className="w-3.5 h-3.5" /> Premium artisan
            </div>
            <div className="display text-3xl mt-2">Batisur Pro</div>

            <div className="flex items-baseline gap-2 mt-3">
              <div className="display text-6xl">{premiumPrice}€</div>
              <div className="mono text-sm text-zinc-400">/ {premiumDays} jours</div>
            </div>

            <ul className="mt-6 space-y-3 text-sm flex-1">
              {[
                ["Mise en avant prioritaire", "Apparaissez en haut de l'annuaire"],
                ["Badge Premium vérifié", "Inspirez confiance dès le premier regard"],
                ["Devis illimités garantis", "Aucune limite de prospection"],
                ["Support prioritaire", "Réponse sous 4h ouvrées"],
              ].map(([t, d]) => (
                <li key={t} className="flex items-start gap-3">
                  <div className="w-5 h-5 mt-0.5 bg-[#FF4D00] flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-white" strokeWidth={3} />
                  </div>
                  <div>
                    <div className="font-semibold">{t}</div>
                    <div className="text-zinc-400 text-xs">{d}</div>
                  </div>
                </li>
              ))}
            </ul>

            <button
              onClick={() => subscribe("premium")}
              disabled={!!busy}
              className="btn-batisur w-full mt-7 flex items-center justify-center gap-2"
              data-testid="premium-subscribe"
            >
              {busy === "premium" ? "Redirection…" : (
                <>Activer Premium <ArrowUpRight className="w-4 h-4" /></>
              )}
            </button>
            <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mt-3 text-center">
              Paiement Stripe · Sans engagement
            </div>
          </div>

          {/* Gold (Pack Confiance) plan */}
          <div className="border-2 border-amber-500 bg-gradient-to-br from-amber-50 to-white p-7 relative flex flex-col">
            <div className="absolute -top-3 left-7 bg-amber-500 text-[#0A0A0B] px-3 py-1 mono text-[10px] uppercase tracking-widest font-semibold">
              Élite
            </div>
            <div className="flex items-center gap-2 mono text-[11px] uppercase tracking-widest text-amber-700">
              <Award className="w-3.5 h-3.5" /> Pack Confiance · Or
            </div>
            <div className="display text-3xl mt-2">Artisan Or</div>

            <div className="flex items-baseline gap-2 mt-3">
              <div className="display text-6xl text-amber-700">{goldPrice}€</div>
              <div className="mono text-sm text-zinc-500">/ {goldDays} jours</div>
            </div>

            <ul className="mt-6 space-y-3 text-sm flex-1">
              {[
                ["Badge Or exclusif", "Le plus haut signal de confiance Batisur"],
                ["Tête d'annuaire", "Avant les Premium, avant tout le monde"],
                ["SIRET vérifié affiché", "Dénomination officielle visible sur la fiche"],
                ["Mise en avant landing", "Sélection éditoriale sur la page d'accueil"],
                ["Accompagnement dédié", "Conseiller Batisur joignable directement"],
              ].map(([t, d]) => (
                <li key={t} className="flex items-start gap-3">
                  <div className="w-5 h-5 mt-0.5 bg-amber-500 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-white" strokeWidth={3} />
                  </div>
                  <div>
                    <div className="font-semibold text-[#0A0A0B]">{t}</div>
                    <div className="text-zinc-600 text-xs">{d}</div>
                  </div>
                </li>
              ))}
            </ul>

            {/* Eligibility */}
            <div className="mt-5 border-2 border-amber-500 bg-white p-3 text-xs space-y-1.5">
              <div className="mono text-[10px] uppercase tracking-widest text-amber-700">Conditions d'accès</div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  {elig.siret_verified ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Lock className="w-3.5 h-3.5 text-zinc-400" />}
                  SIRET vérifié
                </span>
                <span className={elig.siret_verified ? "text-emerald-700 font-semibold" : "text-zinc-500"}>
                  {elig.siret_verified ? "OK" : "Requis"}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  {elig.completed_count >= goldMin ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Lock className="w-3.5 h-3.5 text-zinc-400" />}
                  {goldMin} missions terminées
                </span>
                <span className={elig.completed_count >= goldMin ? "text-emerald-700 font-semibold" : "text-zinc-500"}>
                  {elig.completed_count} / {goldMin}
                </span>
              </div>
            </div>

            <button
              onClick={() => subscribe("gold")}
              disabled={!!busy || !elig.eligible}
              className={`w-full mt-5 flex items-center justify-center gap-2 px-5 py-3 border-2 mono text-[11px] uppercase tracking-widest font-semibold transition ${
                elig.eligible
                  ? "bg-amber-500 border-amber-600 text-[#0A0A0B] hover:bg-amber-400"
                  : "bg-zinc-100 border-zinc-300 text-zinc-400 cursor-not-allowed"
              }`}
              data-testid="gold-subscribe"
              title={!elig.eligible ? "Vous devez vérifier votre SIRET et avoir terminé au moins 10 missions." : ""}
            >
              {busy === "gold" ? "Redirection…" : elig.eligible ? (
                <>Passer Or <ArrowUpRight className="w-4 h-4" /></>
              ) : (
                <><Lock className="w-3.5 h-3.5" /> Non éligible</>
              )}
            </button>
            {!elig.eligible && (
              <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mt-3 text-center" data-testid="gold-not-eligible">
                {!elig.siret_verified ? "Vérifiez votre SIRET dans votre profil." : `Terminez ${goldMin - elig.completed_count} mission(s) supplémentaire(s).`}
              </div>
            )}
          </div>
        </div>

        <div className="mt-12 border-2 border-[#0A0A0B] bg-white p-8 grid md:grid-cols-3 gap-6">
          {[
            [ShieldCheck, "Vérifié & traçable", "Les Or ont un SIRET certifié contre la base officielle entreprises.gouv."],
            [Crown, "Top de l'annuaire", "Or > Premium > standard — visibilité ordonnée pour rassurer le particulier."],
            [Sparkles, "Conversion accrue", "Les profils mis en avant reçoivent significativement plus de demandes (estimation)."],
          ].map(([Icon, t, d]) => (
            <div key={t}>
              <Icon className="w-7 h-7 text-[#FF4D00]" />
              <div className="display text-xl mt-3">{t}</div>
              <div className="text-sm text-zinc-600 mt-1">{d}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
